import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck, MailCheck, Trash2, AlertTriangle } from "lucide-react";
import { api, apiErr } from "../api";
import { Page } from "../components/Layout";

const input = "mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-violet-300";

export function CancellazioneAccountRichiesta() {
  const [params] = useSearchParams();
  const anon = params.get("anon");
  return (
    <Page>
      <div className="max-w-md mx-auto pt-10 px-1 text-center" data-testid="deletion-requested-page">
        <MailCheck size={44} color="#7C1FD6" className="mx-auto mb-3" />
        <h1 className="serif text-2xl mb-2">Richiesta ricevuta</h1>
        <p className="text-sm text-gray-700">Abbiamo registrato la tua richiesta di eliminazione account. L'account è ora <b>sospeso</b> e sei stato disconnesso.</p>
        <div className="card p-4 mt-4 text-left text-sm text-gray-700 space-y-2">
          <p>• Hai <b>14 giorni</b> per ripensarci.</p>
          {anon && <p>• Se non annulli, i dati personali verranno eliminati definitivamente il <b data-testid="deletion-anon-date">{anon}</b>.</p>}
          <p>• Ti abbiamo inviato un'<b>email con il link sicuro</b> per annullare la richiesta.</p>
        </div>
        <Link to="/" className="btn-primary inline-block mt-6" data-testid="deletion-goto-home">Torna alla home</Link>
      </div>
    </Page>
  );
}

export function AnnullaCancellazione() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("idle");

  const submit = async () => {
    if (!token) return;
    setStatus("loading");
    try {
      await api.post("/account/cancel-deletion", { token });
      setStatus("done");
    } catch (er) {
      toast.error(apiErr(er));
      setStatus("error");
    }
  };

  return (
    <Page>
      <div className="max-w-md mx-auto pt-10 px-1" data-testid="cancel-deletion-page">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={20} color="#7C1FD6" />
          <h1 className="serif text-2xl">Annulla eliminazione account</h1>
        </div>
        {!token ? (
          <div className="card p-5 text-center" data-testid="cancel-invalid">
            <p className="text-sm text-gray-700">Link non valido o scaduto.</p>
          </div>
        ) : status === "done" ? (
          <div className="card p-5 text-center" data-testid="cancel-done">
            <MailCheck size={40} color="#7C1FD6" className="mx-auto mb-3" />
            <p className="text-sm text-gray-700">La richiesta di eliminazione è stata annullata. Il tuo account è di nuovo attivo.</p>
            <Link to="/accedi" className="btn-primary inline-block mt-4" data-testid="cancel-goto-login">Accedi</Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">Confermi di voler annullare la richiesta di eliminazione del tuo account MAS? L'account tornerà utilizzabile e potrai accedere con un nuovo login.</p>
            <button data-testid="cancel-deletion-submit" disabled={status === "loading"} onClick={submit} className="btn-primary w-full">
              {status === "loading" ? "Attendere…" : "Annulla richiesta di cancellazione"}
            </button>
          </>
        )}
        <p className="text-center text-xs text-gray-400 mt-6">
          <Link to="/accedi" className="underline" data-testid="cancel-back-login">Torna al login</Link>
        </p>
      </div>
    </Page>
  );
}

export function PasswordDimenticata() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (er) {
      toast.error(apiErr(er));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <div className="max-w-md mx-auto pt-10 px-1" data-testid="forgot-password-page">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={20} color="#7C1FD6" />
          <h1 className="serif text-2xl">Password dimenticata</h1>
        </div>
        {!sent ? (
          <>
            <p className="text-sm text-gray-500 mb-4">Inserisci l'email del tuo account MAS: ti invieremo un link per reimpostare la password.</p>
            <form onSubmit={submit} className="space-y-4">
              <label className="block text-sm">Email
                <input data-testid="forgot-email" type="email" required className={input} value={email}
                  onChange={(e) => setEmail(e.target.value)} autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username" />
              </label>
              <button data-testid="forgot-submit" disabled={loading} className="btn-primary w-full">
                {loading ? "Attendere…" : "Invia link di reset"}
              </button>
            </form>
          </>
        ) : (
          <div className="card p-5 text-center" data-testid="forgot-sent">
            <MailCheck size={40} color="#7C1FD6" className="mx-auto mb-3" />
            <p className="text-sm text-gray-700">Se l'indirizzo inserito è associato a un account MAS, riceverai a breve un'email con le istruzioni per reimpostare la password.</p>
            <p className="text-xs text-gray-400 mt-2">Controlla anche la cartella spam. Il link è valido 30 minuti.</p>
          </div>
        )}
        <p className="text-center text-xs text-gray-400 mt-6">
          <Link to="/accedi" className="underline" data-testid="forgot-back-login">Torna al login</Link>
        </p>
      </div>
    </Page>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [f, setF] = useState({ new_password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Link non valido o scaduto.");
    if (f.new_password.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri.");
    if (f.new_password !== f.confirm) return toast.error("Le password non coincidono.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: f.new_password });
      toast.success("Password reimpostata con successo. Ora puoi accedere.");
      navigate("/accedi", { replace: true });
    } catch (er) {
      toast.error(apiErr(er));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <div className="max-w-md mx-auto pt-10 px-1" data-testid="reset-password-page">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={20} color="#7C1FD6" />
          <h1 className="serif text-2xl">Imposta nuova password</h1>
        </div>
        {!token ? (
          <div className="card p-5 text-center" data-testid="reset-invalid">
            <p className="text-sm text-gray-700">Link non valido o scaduto.</p>
            <Link to="/password-dimenticata" className="btn-primary inline-block mt-4" data-testid="reset-request-new">Richiedi un nuovo link</Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">Scegli una nuova password per il tuo account MAS (almeno 8 caratteri).</p>
            <form onSubmit={submit} className="space-y-4">
              <label className="block text-sm">Nuova password
                <div className="relative">
                  <input data-testid="reset-new-password" type={showPw ? "text" : "password"} required minLength={8}
                    className={`${input} pr-12`} value={f.new_password}
                    onChange={(e) => setF({ ...f, new_password: e.target.value })}
                    autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Nascondi password" : "Mostra password"}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-violet-600 focus:outline-none">
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </label>
              <label className="block text-sm">Conferma password
                <input data-testid="reset-confirm-password" type={showPw ? "text" : "password"} required minLength={8}
                  className={input} value={f.confirm}
                  onChange={(e) => setF({ ...f, confirm: e.target.value })}
                  autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="new-password" />
              </label>
              <button data-testid="reset-submit" disabled={loading} className="btn-primary w-full">
                {loading ? "Attendere…" : "Reimposta password"}
              </button>
            </form>
          </>
        )}
        <p className="text-center text-xs text-gray-400 mt-6">
          <Link to="/accedi" className="underline" data-testid="reset-back-login">Torna al login</Link>
        </p>
      </div>
    </Page>
  );
}


// M2-A4 — Pagina pubblica: richiesta eliminazione account/dati (senza login, verifica via email).
export function EliminaAccount() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/account/public-delete-request", { email: email.trim() });
      setSent(true);
    } catch (er) {
      toast.error(apiErr(er));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <div className="max-w-md mx-auto pt-10 px-1" data-testid="delete-account-page">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 size={20} color="#7C1FD6" />
          <h1 className="serif text-2xl">Richiedi l'eliminazione del tuo account MAS</h1>
        </div>
        {!sent ? (
          <>
            <p className="text-sm text-gray-500 mb-4">
              Inserisci l'email del tuo account MAS. Per sicurezza ti invieremo un'email con un link per
              <b> confermare</b> la richiesta: solo dopo la conferma la cancellazione verrà avviata.
            </p>
            <div className="card p-4 mb-4 text-left text-[13px] text-gray-700 space-y-1.5" data-testid="delete-account-info">
              <p>• Riguarda il tuo <b>account MAS</b> e i dati personali collegati.</p>
              <p>• La richiesta deve essere <b>verificata via email</b>.</p>
              <p>• Dopo la conferma, l'<b>accesso viene sospeso</b>.</p>
              <p>• Hai <b>14 giorni</b> di ripensamento.</p>
              <p>• Riceverai un'email con il <b>link per annullare</b> la richiesta.</p>
              <p>• Dopo 14 giorni i dati personali cancellabili vengono <b>anonimizzati</b> in modo irreversibile.</p>
              <p>• Alcuni dati economici/documentali possono essere <b>conservati</b> quando previsto dalla legge.</p>
              <p>• <b>Non</b> vengono emessi automaticamente rimborsi.</p>
              <p>• Eventuali <b>impegni attivi</b> (prenotazioni future, abbonamento Resident attivo, crediti) vanno risolti prima.</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <label className="block text-sm">Email
                <input data-testid="delete-account-email" type="email" required className={input} value={email}
                  onChange={(e) => setEmail(e.target.value)} autoCapitalize="none" autoCorrect="off" spellCheck={false} autoComplete="username" />
              </label>
              <button data-testid="delete-account-submit" disabled={loading} className="btn-primary w-full">
                {loading ? "Attendere…" : "Invia richiesta di eliminazione"}
              </button>
            </form>
          </>
        ) : (
          <div className="card p-5 text-center" data-testid="delete-account-sent">
            <MailCheck size={40} color="#7C1FD6" className="mx-auto mb-3" />
            <p className="text-sm text-gray-700">Se l'indirizzo inserito è associato a un account MAS, riceverai a breve un'email con il link per confermare la richiesta di eliminazione.</p>
            <p className="text-xs text-gray-400 mt-2">Controlla anche la cartella spam. Il link è valido 30 minuti e può essere usato una sola volta.</p>
          </div>
        )}
        <p className="text-center text-xs text-gray-400 mt-6 space-x-2">
          <Link to="/" className="underline" data-testid="delete-account-home">Torna alla home</Link>
          <span>·</span>
          <Link to="/privacy" className="underline" data-testid="delete-account-privacy">Privacy</Link>
          <span>·</span>
          <Link to="/termini" className="underline" data-testid="delete-account-termini">Termini</Link>
        </p>
      </div>
    </Page>
  );
}

export function EliminaAccountConferma() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [errMsg, setErrMsg] = useState("");

  const confirm = async () => {
    if (!token) return;
    setStatus("loading");
    try {
      await api.post("/account/public-delete-confirm", { token });
      setStatus("done");
    } catch (er) {
      setErrMsg(apiErr(er));
      setStatus("error");
    }
  };

  return (
    <Page>
      <div className="max-w-md mx-auto pt-10 px-1" data-testid="delete-confirm-page">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 size={20} color="#7C1FD6" />
          <h1 className="serif text-2xl">Conferma eliminazione account</h1>
        </div>
        {!token ? (
          <div className="card p-5 text-center" data-testid="delete-confirm-invalid">
            <p className="text-sm text-gray-700">Link non valido o scaduto.</p>
            <Link to="/elimina-account" className="btn-primary inline-block mt-4" data-testid="delete-confirm-request-new">Richiedi un nuovo link</Link>
          </div>
        ) : status === "done" ? (
          <div className="card p-5 text-center" data-testid="delete-confirm-done">
            <MailCheck size={40} color="#7C1FD6" className="mx-auto mb-3" />
            <p className="text-sm text-gray-700">La richiesta è stata avviata. Il tuo account è ora <b>sospeso</b>.</p>
            <div className="text-left text-sm text-gray-700 space-y-1.5 mt-3">
              <p>• Hai <b>14 giorni</b> per ripensarci.</p>
              <p>• Ti abbiamo inviato un'<b>email con il link sicuro</b> per annullare la richiesta.</p>
              <p>• Trascorsi i 14 giorni, i dati personali cancellabili vengono anonimizzati in modo irreversibile.</p>
            </div>
            <Link to="/" className="btn-primary inline-block mt-5" data-testid="delete-confirm-home">Torna alla home</Link>
          </div>
        ) : status === "error" ? (
          <div className="card p-5 text-center" data-testid="delete-confirm-error">
            <AlertTriangle size={40} color="#b91c1c" className="mx-auto mb-3" />
            <p className="text-sm text-gray-700" data-testid="delete-confirm-error-msg">{errMsg || "Link non valido o scaduto."}</p>
            <Link to="/elimina-account" className="btn-outline inline-block mt-4" data-testid="delete-confirm-retry">Ricomincia</Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">
              Confermi di voler eliminare il tuo account MAS? Alla conferma l'accesso verrà <b>sospeso</b> e partiranno
              i <b>14 giorni</b> di ripensamento. Riceverai un'email con il link per annullare.
            </p>
            <button data-testid="delete-confirm-submit" disabled={status === "loading"} onClick={confirm} className="btn-primary w-full">
              {status === "loading" ? "Attendere…" : "Conferma eliminazione account"}
            </button>
          </>
        )}
        <p className="text-center text-xs text-gray-400 mt-6">
          <Link to="/" className="underline" data-testid="delete-confirm-back-home">Torna alla home</Link>
        </p>
      </div>
    </Page>
  );
}
